/**
 * useShipmentActivityStream — subscribe to a shipment's live activity feed via
 * Server-Sent Events.
 *
 * Backend contract (GET /api/v2/shipments/:id/activity/stream):
 *   - emits `connected` event once on open with `{ shipmentId }`
 *   - emits `activity` events as new items land in chat_messages, with the
 *     same shape as GET /activity items (id, kind, role, content, payload, createdAt)
 *   - sends `: heartbeat` comment lines every ~25s to keep the connection open
 *
 * Usage:
 *   const { items, connected, error } = useShipmentActivityStream(shipmentId)
 *
 * `items` accumulates incoming activity in arrival order. Combine with the
 * initial GET /activity fetch in the page to seed history; the stream
 * supplies everything that arrives after the page loads.
 *
 * EventSource doesn't support custom headers (notably `Authorization`), so
 * authentication is handled by appending the Clerk session token to the URL
 * as `?token=...`. The backend's clerkAuth middleware accepts either header
 * or query token. (If your backend currently only accepts the header, this
 * hook upgrades the auth flow there — see clerkAuth middleware.)
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { friendlyErrorMessage } from '../utils/friendlyError'
import { useAuth } from '@clerk/clerk-react'

const API_BASE = import.meta.env.VITE_API_URL || ''

export function useShipmentActivityStream(shipmentId, { enabled = true } = {}) {
  const { getToken, isSignedIn } = useAuth()
  const [items, setItems] = useState([])
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState(null)
  const sourceRef = useRef(null)
  // Clerk's getToken identity is not guaranteed stable across renders. If we
  // listed it in the effect's dep array directly, every parent re-render would
  // tear down + re-open the EventSource, leaking concurrent streams that each
  // re-push the same chat_messages rows → "Hi" appears 2×/4×/8× as the user
  // navigates between shipments. Capture it in a ref so the effect closes
  // over the latest value without re-subscribing on identity churn.
  const getTokenRef = useRef(getToken)
  useEffect(() => { getTokenRef.current = getToken }, [getToken])

  // Reset accumulated items when shipmentId changes
  useEffect(() => {
    setItems([])
    setConnected(false)
    setError(null)
  }, [shipmentId])

  useEffect(() => {
    if (!enabled || !shipmentId || !isSignedIn) return

    // Belt-and-braces: if a previous EventSource is still around (cleanup
    // race or HMR), tear it down before opening a new one. Without this a
    // stuck connection from the previous render keeps pushing the same
    // activity rows into setItems alongside the new one.
    if (sourceRef.current) {
      try { sourceRef.current.close() } catch { /* ignore */ }
      sourceRef.current = null
    }

    let cancelled = false
    let es = null

    ;(async () => {
      try {
        const token = await getTokenRef.current()
        if (cancelled) return
        const url = `${API_BASE}/api/v2/shipments/${shipmentId}/activity/stream${
          token ? `?token=${encodeURIComponent(token)}` : ''
        }`
        es = new EventSource(url, { withCredentials: false })
        sourceRef.current = es

        es.addEventListener('connected', () => {
          if (!cancelled) setConnected(true)
        })

        es.addEventListener('activity', (e) => {
          if (cancelled) return
          try {
            const item = JSON.parse(e.data)
            // Dedup by id at ingestion time. Two concurrent streams (e.g.
            // mid-transition leftover + new) push the same persisted UUID;
            // without this guard the hook's items array accumulates copies
            // and downstream consumers see Nx the same message.
            setItems(prev =>
              prev.some(p => p.id === item.id) ? prev : [...prev, item]
            )
          } catch (err) {
            // bad JSON from server — log + skip
            // eslint-disable-next-line no-console
            console.warn('useShipmentActivityStream: bad payload', err)
          }
        })

        es.onerror = () => {
          if (cancelled) return
          // EventSource auto-reconnects; surface a soft error so the UI can
          // optionally show a "reconnecting…" state. Don't tear down — the
          // browser will retry on its own.
          setError('Connection lost — reconnecting…')
          setConnected(false)
        }
      } catch (err) {
        // Hook returns `error` as a user-safe string. Run the Error through
        // friendlyErrorMessage so raw bodies / stack traces don't leak.
        if (!cancelled) setError(friendlyErrorMessage(err, 'Failed to open stream'))
      }
    })()

    return () => {
      cancelled = true
      if (es) {
        try { es.close() } catch { /* ignore */ }
      }
      if (sourceRef.current === es) sourceRef.current = null
    }
  }, [shipmentId, isSignedIn, enabled])

  /** Manually clear accumulated items (useful for tests or "clear chat" UX). */
  const clear = useCallback(() => setItems([]), [])

  return { items, connected, error, clear }
}
